/**
 * GroupFacturationView
 * Component for managing grouped invoicing by monthly billing contacts
 *
 * Flow:
 * 1. Shows all billing contacts with FACTURE MENSUELLE role that have eligible affaires
 * 2. User selects a contact to see their eligible affaires
 * 3. User selects affaires and generates a grouped invoice
 * 4. User can also view invoice history for each contact
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  MapPinIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CurrencyEuroIcon,
  ChatBubbleBottomCenterTextIcon,
  CubeIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingLibraryIcon,
  EnvelopeIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { useMonthlyBillingContacts, useMonthlyBillingEligible, useContactInvoices, useCancelInvoice, invoiceKeys } from '../../hooks/useInvoices';
import { GenerateMonthlyInvoiceModal } from '../GenerateInvoice/GenerateMonthlyInvoiceModal';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import { InvoiceDetailModal } from '../InvoiceDetailModal/InvoiceDetailModal';
import { downloadInvoicePdf } from '../../api/facturation.api';
import type { MonthlyBillingContact } from '../../api/facturation.api';
import type { EligibleAffaire, Invoice } from '../../types/facturation.types';

// Status filter type
type InvoiceStatusFilter = 'all' | 'draft' | 'generated' | 'paid';

// BillingContact type matching the GenerateMonthlyInvoiceModal interface
interface BillingContactForModal {
  id: string;
  name: string;
  address: string | null;
  vat_number: string | null;
  email: string | null;
  pays: string | null;
}
import { useQueryClient } from '@tanstack/react-query';
import '../../facturation.css';

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Contact Card component
interface ContactCardProps {
  contact: MonthlyBillingContact;
  onClick: () => void;
}

const ContactCard: React.FC<ContactCardProps> = ({ contact, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="fact-group-contact-card"
    >
      <div className="fact-group-contact-icon">
        {contact.company ? (
          <BuildingOfficeIcon className="w-6 h-6" />
        ) : (
          <UserGroupIcon className="w-6 h-6" />
        )}
      </div>
      <div className="fact-group-contact-info">
        <h3 className="fact-group-contact-name">
          {contact.company || contact.contact_name}
        </h3>
        {contact.company && contact.contact_name && (
          <p className="fact-group-contact-person">{contact.contact_name}</p>
        )}
        {contact.vat_number && (
          <p className="fact-group-contact-vat">{contact.vat_number}</p>
        )}
      </div>
      <div className="fact-group-contact-badge">
        <DocumentTextIcon className="w-4 h-4" />
        <span>{contact.affaire_count} dossier{contact.affaire_count > 1 ? 's' : ''}</span>
      </div>
    </button>
  );
};

// Affaire Row component
interface AffaireRowProps {
  affaire: EligibleAffaire;
  selected: boolean;
  highlighted?: boolean;
  rowRef?: React.Ref<HTMLDivElement> | null;
  onToggle: () => void;
  onOpenFacturation: (affaireId: string) => void;
  onOpenTracker: (affaireId: string) => void;
}

const AffaireRow: React.FC<AffaireRowProps> = ({ affaire, selected, highlighted, rowRef, onToggle, onOpenFacturation, onOpenTracker }) => {
  const handleFacturationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenFacturation(affaire.affaire_id);
  };

  const handleTrackerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenTracker(affaire.affaire_id);
  };

  return (
    <div
      ref={rowRef ?? null}
      className={`fact-group-affaire-row ${selected ? 'selected' : ''}`}
      data-highlight={highlighted ? 'true' : undefined}
      onClick={onToggle}
    >
      <div className="fact-group-affaire-checkbox">
        {selected && <CheckIcon className="w-4 h-4" />}
      </div>
      <div className="fact-group-affaire-info">
        <span className="fact-group-affaire-id">{affaire.k_id_dossier}</span>
        <span className="fact-group-affaire-address">
          <MapPinIcon className="w-4 h-4" />
          {affaire.adresse_bien || 'Adresse non renseignee'}
        </span>
      </div>
      <div className="fact-group-affaire-actions">
        <button
          className="fact-group-affaire-action-btn"
          onClick={handleFacturationClick}
          title="Voir les produits"
        >
          <CubeIcon className="w-4 h-4" />
        </button>
        <button
          className="fact-group-affaire-action-btn"
          onClick={handleTrackerClick}
          title="Voir le tracker"
        >
          <ChatBubbleBottomCenterTextIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="fact-group-affaire-amount">
        {formatCurrency(affaire.total_tvac)}
      </div>
    </div>
  );
};

// Format date for display
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Tab type for selected contact view
type ContactViewTab = 'eligible' | 'history';

export const GroupFacturationView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Fetch all monthly billing contacts
  const { data: contacts, isLoading: contactsLoading, error: contactsError } = useMonthlyBillingContacts();

  // Selected contact state
  const [selectedContact, setSelectedContact] = useState<MonthlyBillingContact | null>(null);

  // Active tab when contact is selected
  const [activeTab, setActiveTab] = useState<ContactViewTab>('eligible');

  // Highlight a specific affaire row when arriving via the State-A CTA on
  // ProductsEmbeddedView. Cleared after 8s.
  const [highlightAffaireId, setHighlightAffaireId] = useState<string | null>(null);
  const highlightedRowRef = useRef<HTMLDivElement | null>(null);

  // Handle inbound navigation state from ProductsEmbeddedView: preselect the
  // monthly billing contact and remember which affaire to highlight.
  useEffect(() => {
    const navState = location.state as {
      preselectedContactId?: string;
      highlightAffaireId?: string;
    } | null;
    if (!navState?.preselectedContactId || !contacts) return;
    const target = contacts.find(c => c.contact_id === navState.preselectedContactId);
    if (target) {
      setSelectedContact(target);
      setActiveTab('eligible');
      setHighlightAffaireId(navState.highlightAffaireId ?? null);
      // Clear the navigation state so a tab switch later doesn't re-trigger.
      window.history.replaceState({}, document.title);
    }
  }, [location.state, contacts]);

  // Auto-clear highlight after 8s and scroll the row into view once it
  // mounts.
  useEffect(() => {
    if (!highlightAffaireId) return;
    const scrollTimer = setTimeout(() => {
      highlightedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    const clearTimer = setTimeout(() => setHighlightAffaireId(null), 8000);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightAffaireId]);

  // Fetch eligible affaires for selected contact
  const { data: eligibleAffaires, isLoading: affairesLoading } = useMonthlyBillingEligible(
    selectedContact?.contact_id || ''
  );

  // Fetch invoice history for selected contact
  const { data: contactInvoicesData, isLoading: invoicesLoading } = useContactInvoices(
    selectedContact?.contact_id || ''
  );
  const contactInvoices = contactInvoicesData?.invoices || [];

  // Cancel invoice mutation
  const cancelMutation = useCancelInvoice();

  // Selected affaires for invoicing
  const [selectedAffaireIds, setSelectedAffaireIds] = useState<Set<string>>(new Set());

  // Modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // PDF Preview state
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);

  // Invoice Detail Modal state
  const [selectedInvoiceForDetail, setSelectedInvoiceForDetail] = useState<Invoice | null>(null);

  // Status filter for invoices
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<InvoiceStatusFilter>('all');

  // Filtered invoices based on status
  const filteredInvoices = useMemo(() => {
    if (!contactInvoices) return [];
    if (invoiceStatusFilter === 'all') return contactInvoices;
    if (invoiceStatusFilter === 'draft') return contactInvoices.filter(i => i.status === 'DRAFT');
    if (invoiceStatusFilter === 'generated') return contactInvoices.filter(i => i.status === 'SENT' || i.status === 'GENERATED');
    if (invoiceStatusFilter === 'paid') return contactInvoices.filter(i => i.status === 'PAID');
    return contactInvoices;
  }, [contactInvoices, invoiceStatusFilter]);

  // Handle preview click
  const handlePreviewInvoice = (invoice: Invoice) => {
    setPreviewInvoice(invoice);
    setShowPdfPreview(true);
  };

  // Handle invoice row click to open detail modal
  const handleInvoiceRowClick = (invoice: Invoice) => {
    setSelectedInvoiceForDetail(invoice);
  };

  // Handle download PDF
  const handleDownloadPdf = async (invoice: Invoice, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      const blob = await downloadInvoicePdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = invoice.invoice_number
        ? `facture_${invoice.invoice_number.replace(/\//g, '-')}.pdf`
        : `facture_brouillon_${invoice.id}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Handle cancel invoice
  const handleCancelInvoice = async (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    const reason = prompt('Raison de l\'annulation:');
    if (reason) {
      try {
        await cancelMutation.mutateAsync({ id: invoice.id, reason, currentStatus: invoice.status });
        // Refetch invoices for the contact
        if (selectedContact) {
          queryClient.invalidateQueries({
            queryKey: invoiceKeys.contactInvoices(selectedContact.contact_id)
          });
        }
      } catch (error) {
        console.error('Cancel failed:', error);
      }
    }
  };

  // Format date with icon
  const formatDateWithIcon = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-BE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get status badge class
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PAID': return 'fact-status-badge paid';
      case 'SENT': return 'fact-status-badge sent';
      case 'GENERATED': return 'fact-status-badge generated';
      case 'CANCELLED': return 'fact-status-badge cancelled';
      case 'DRAFT': return 'fact-status-badge draft';
      default: return 'fact-status-badge';
    }
  };

  // Get status label
  const getStatusLabelForBadge = (status: string) => {
    switch (status) {
      case 'PAID': return 'Paye';
      case 'SENT': return 'Envoye';
      case 'GENERATED': return 'Genere';
      case 'CANCELLED': return 'Annule';
      case 'DRAFT': return 'Brouillon';
      default: return status;
    }
  };

  // Get Falco badge status
  const getFalcoBadge = (invoice: Invoice) => {
    if (invoice.falco_status === 'UPLOADED') return { class: 'success', title: 'Falco: UPLOADED' };
    if (invoice.falco_status === 'FAILED') return { class: 'error', title: `Falco: ${invoice.falco_error || 'Erreur'}` };
    return { class: 'inactive', title: 'Falco: Non envoyé' };
  };

  // Get Email badge status
  const getEmailBadge = (invoice: Invoice) => {
    if (invoice.email_sent_at) return { class: 'success', title: `Email envoyé le ${formatDate(invoice.email_sent_at)}` };
    return { class: 'inactive', title: 'Email non envoyé' };
  };

  // Get Peppol badge status
  const getPeppolBadge = (invoice: Invoice) => {
    if (invoice.peppol_status === 'DELIVERED') return { class: 'success', title: 'Peppol: Livré' };
    if (invoice.peppol_status === 'FAILED') return { class: 'error', title: `Peppol: ${invoice.peppol_error || 'Erreur'}` };
    if (invoice.peppol_status === 'NOT_REGISTERED') return { class: 'inactive', title: 'Peppol: Client non inscrit' };
    return { class: 'inactive', title: 'Peppol: N/A' };
  };

  // Calculate totals for selected affaires
  const selectedTotals = useMemo(() => {
    if (!eligibleAffaires) return { htva: 0, tva: 0, tvac: 0 };

    return eligibleAffaires
      .filter(a => selectedAffaireIds.has(a.affaire_id))
      .reduce((acc, a) => ({
        htva: acc.htva + a.total_htva,
        tva: acc.tva + a.total_tva,
        tvac: acc.tvac + a.total_tvac,
      }), { htva: 0, tva: 0, tvac: 0 });
  }, [eligibleAffaires, selectedAffaireIds]);

  // Handle contact selection
  const handleContactSelect = (contact: MonthlyBillingContact) => {
    setSelectedContact(contact);
    setSelectedAffaireIds(new Set());
    setActiveTab('eligible');
  };

  // Handle back to contact list
  const handleBack = () => {
    setSelectedContact(null);
    setSelectedAffaireIds(new Set());
    setActiveTab('eligible');
  };

  // Toggle affaire selection
  const toggleAffaireSelection = (affaireId: string) => {
    setSelectedAffaireIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(affaireId)) {
        newSet.delete(affaireId);
      } else {
        newSet.add(affaireId);
      }
      return newSet;
    });
  };

  // Select all affaires
  const selectAllAffaires = () => {
    if (!eligibleAffaires) return;
    setSelectedAffaireIds(new Set(eligibleAffaires.map(a => a.affaire_id)));
  };

  // Deselect all affaires
  const deselectAllAffaires = () => {
    setSelectedAffaireIds(new Set());
  };

  // Convert MonthlyBillingContact to BillingContact format for modal
  const getBillingContactForModal = (): BillingContactForModal | undefined => {
    if (!selectedContact) return undefined;

    return {
      id: selectedContact.contact_id,
      name: selectedContact.company || selectedContact.contact_name,
      address: selectedContact.address,
      vat_number: selectedContact.vat_number,
      email: selectedContact.email,
      pays: selectedContact.pays,
    };
  };

  // Handle invoice generation button click
  const handleGenerateInvoice = () => {
    setShowInvoiceModal(true);
  };

  // Handle invoice modal success
  const handleInvoiceSuccess = () => {
    setShowInvoiceModal(false);
    // Refresh data
    queryClient.invalidateQueries({ queryKey: invoiceKeys.monthlyContacts() });
    if (selectedContact) {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.monthlyEligible(selectedContact.contact_id)
      });
      // Also refresh the invoices list so new invoice appears in "Factures emises"
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.contactInvoices(selectedContact.contact_id)
      });
    }
    // Reset selection
    setSelectedAffaireIds(new Set());
  };

  // Navigate to facturation (products) for a specific affaire
  const handleOpenFacturation = (affaireId: string) => {
    const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
    navigate(`${basePath}/facturation`, {
      state: {
        preselectedAffaireId: parseInt(affaireId, 10),
        from: location.pathname,
        fromTab: 'group-facturation'
      }
    });
  };

  // Navigate to tracker for a specific affaire
  const handleOpenTracker = (affaireId: string) => {
    const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
    navigate(`${basePath}/tracker/${affaireId}`, {
      state: {
        from: location.pathname
      }
    });
  };

  // Render loading state
  if (contactsLoading) {
    return (
      <div className="fact-group-container">
        <div className="fact-group-loading">
          <div className="fact-group-spinner" />
          <p>Chargement des contacts...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (contactsError) {
    return (
      <div className="fact-group-container">
        <div className="fact-group-error">
          <ExclamationTriangleIcon className="w-12 h-12" />
          <h3>Erreur de chargement</h3>
          <p>Impossible de charger les contacts de facturation.</p>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!contacts || contacts.length === 0) {
    return (
      <div className="fact-group-container">
        <div className="fact-group-empty">
          <UserGroupIcon className="w-16 h-16" />
          <h3>Aucun client mensuel</h3>
          <p>
            Aucun contact avec le role "FACTURE MENSUELLE" n'a de dossiers avec
            le statut "facturation mensuelle" a facturer.
          </p>
        </div>
      </div>
    );
  }

  // Render affaire selection view
  if (selectedContact) {
    return (
      <div className="fact-group-container">
        {/* Back button and header */}
        <div className="fact-group-header">
          <button onClick={handleBack} className="fact-group-back-btn">
            <ArrowLeftIcon className="w-5 h-5" />
            Retour
          </button>
          <div className="fact-group-header-info">
            <h2>{selectedContact.company || selectedContact.contact_name}</h2>
            {selectedContact.vat_number && (
              <span className="fact-group-header-vat">{selectedContact.vat_number}</span>
            )}
          </div>
        </div>

        {/* Tab switcher - shows dossiers to invoice vs previously generated invoices */}
        <div className="fact-group-tabs">
          <button
            onClick={() => setActiveTab('eligible')}
            className={`fact-group-tab ${activeTab === 'eligible' ? 'active' : ''}`}
          >
            <CurrencyEuroIcon className="w-4 h-4" />
            Dossiers a facturer
            {eligibleAffaires && eligibleAffaires.length > 0 && (
              <span className="fact-group-tab-badge">{eligibleAffaires.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`fact-group-tab ${activeTab === 'history' ? 'active' : ''}`}
          >
            <ClockIcon className="w-4 h-4" />
            Factures emises
            {contactInvoices.length > 0 && (
              <span className="fact-group-tab-badge">{contactInvoices.length}</span>
            )}
          </button>
        </div>

        {/* Eligible affaires tab content */}
        {activeTab === 'eligible' && (
          <>
            {/* Selection controls */}
            <div className="fact-group-controls">
              <div className="fact-group-selection-info">
                {selectedAffaireIds.size} dossier{selectedAffaireIds.size > 1 ? 's' : ''} selectionne{selectedAffaireIds.size > 1 ? 's' : ''}
                {selectedAffaireIds.size > 0 && (
                  <span className="fact-group-selection-total">
                    - Total: {formatCurrency(selectedTotals.tvac)} TVAC
                  </span>
                )}
              </div>
              <div className="fact-group-selection-actions">
                <button
                  onClick={selectAllAffaires}
                  className="fact-group-action-btn"
                  disabled={affairesLoading}
                >
                  Tout selectionner
                </button>
                <button
                  onClick={deselectAllAffaires}
                  className="fact-group-action-btn"
                  disabled={selectedAffaireIds.size === 0}
                >
                  Tout deselectionner
                </button>
              </div>
            </div>

            {/* Affaire list */}
            <div className="fact-group-affaire-list">
              {affairesLoading ? (
                <div className="fact-group-loading-inline">
                  <div className="fact-group-spinner" />
                  <span>Chargement des dossiers...</span>
                </div>
              ) : eligibleAffaires && eligibleAffaires.length > 0 ? (
                eligibleAffaires.map(affaire => {
                  const isHighlighted = highlightAffaireId === affaire.affaire_id;
                  return (
                    <AffaireRow
                      key={affaire.affaire_id}
                      affaire={affaire}
                      selected={selectedAffaireIds.has(affaire.affaire_id)}
                      highlighted={isHighlighted}
                      rowRef={isHighlighted ? highlightedRowRef : null}
                      onToggle={() => toggleAffaireSelection(affaire.affaire_id)}
                      onOpenFacturation={handleOpenFacturation}
                      onOpenTracker={handleOpenTracker}
                    />
                  );
                })
              ) : (
                <div className="fact-group-empty-affaires">
                  <p>Aucun dossier a facturer pour ce contact.</p>
                </div>
              )}
            </div>

            {/* Generate invoice button */}
            <div className="fact-group-footer">
              <button
                onClick={handleGenerateInvoice}
                disabled={selectedAffaireIds.size === 0}
                className="fact-group-generate-btn"
              >
                <DocumentTextIcon className="w-5 h-5" />
                Generer la facture groupee
              </button>
            </div>
          </>
        )}

        {/* Invoice history tab content */}
        {activeTab === 'history' && (
          <>
            {/* Status filters */}
            <div className="fact-controls">
              <div className="fact-controls-left">
                <div className="fact-status-filters">
                  <button
                    className={`fact-status-btn ${invoiceStatusFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setInvoiceStatusFilter('all')}
                  >
                    Tous
                  </button>
                  <button
                    className={`fact-status-btn ${invoiceStatusFilter === 'draft' ? 'active' : ''}`}
                    onClick={() => setInvoiceStatusFilter('draft')}
                  >
                    Brouillons
                  </button>
                  <button
                    className={`fact-status-btn ${invoiceStatusFilter === 'generated' ? 'active' : ''}`}
                    onClick={() => setInvoiceStatusFilter('generated')}
                  >
                    Generes
                  </button>
                  <button
                    className={`fact-status-btn ${invoiceStatusFilter === 'paid' ? 'active' : ''}`}
                    onClick={() => setInvoiceStatusFilter('paid')}
                  >
                    Payes
                  </button>
                </div>
              </div>
            </div>

            {/* Invoice table */}
            <div className="fact-list">
              <div className="fact-results-container">
                {invoicesLoading ? (
                  <div className="fact-group-loading-inline">
                    <div className="fact-group-spinner" />
                    <span>Chargement de l'historique...</span>
                  </div>
                ) : filteredInvoices.length > 0 ? (
                  <div className="fact-table">
                    <div className="fact-table-header">
                      <div>Numero</div>
                      <div>Affaire</div>
                      <div>Client</div>
                      <div>Cree par</div>
                      <div>Date</div>
                      <div className="align-right">Montant</div>
                      <div>Statut</div>
                      <div style={{ textAlign: 'right' }}>Actions</div>
                    </div>
                    {filteredInvoices.map(invoice => (
                      <div
                        key={invoice.id}
                        className="fact-table-row clickable"
                        onClick={() => handleInvoiceRowClick(invoice)}
                      >
                        {/* Numero */}
                        <div>
                          {invoice.invoice_number ? (
                            <span className="fact-invoice-number">{invoice.invoice_number}</span>
                          ) : (
                            <span className="fact-invoice-number fact-draft-label">Brouillon</span>
                          )}
                        </div>

                        {/* Affaire */}
                        <div>
                          {invoice.affaires && invoice.affaires.length > 0 ? (
                            <span className="fact-affaire-number">
                              {invoice.affaires.length > 1
                                ? `${invoice.affaires.length} dossiers`
                                : invoice.affaires[0].affaire_id}
                            </span>
                          ) : (
                            <span className="fact-affaire-number">-</span>
                          )}
                        </div>

                        {/* Client */}
                        <div className="fact-col-client">
                          <span className="fact-client-name">{invoice.billing_name || '-'}</span>
                          {invoice.billing_vat_number && (
                            <span className="fact-client-vat">{invoice.billing_vat_number}</span>
                          )}
                        </div>

                        {/* Cree par */}
                        <div className="fact-col-creator">
                          {invoice.first_name || invoice.last_name ? (
                            <span title={`Créé le ${new Date(invoice.created_at).toLocaleString('fr-BE')}`}>
                              {invoice.first_name || ''} {invoice.last_name || ''}
                            </span>
                          ) : (
                            <span className="fact-no-creator">-</span>
                          )}
                        </div>

                        {/* Date */}
                        <div className="fact-col-date">
                          <ClockIcon className="w-4 h-4" />
                          {formatDateWithIcon(invoice.invoice_date)}
                        </div>

                        {/* Montant */}
                        <div className="fact-col-amount">
                          {formatCurrency(invoice.total_tvac)}
                        </div>

                        {/* Statut */}
                        <div className="fact-col-status">
                          <span className={getStatusBadgeClass(invoice.status)}>
                            {getStatusLabelForBadge(invoice.status)}
                          </span>
                          <div className="fact-delivery-badges">
                            <span
                              className={`fact-delivery-badge ${getFalcoBadge(invoice).class}`}
                              title={getFalcoBadge(invoice).title}
                            >
                              <BuildingLibraryIcon className="w-3.5 h-3.5" />
                            </span>
                            <span
                              className={`fact-delivery-badge ${getEmailBadge(invoice).class}`}
                              title={getEmailBadge(invoice).title}
                            >
                              <EnvelopeIcon className="w-3.5 h-3.5" />
                            </span>
                            <span
                              className={`fact-delivery-badge ${getPeppolBadge(invoice).class}`}
                              title={getPeppolBadge(invoice).title}
                            >
                              <GlobeAltIcon className="w-3.5 h-3.5" />
                            </span>
                          </div>
                          {/* Validator info */}
                          {invoice.validated_by && invoice.validated_at && (
                            <span className="fact-validator-info" title={`Validé le ${new Date(invoice.validated_at).toLocaleString('fr-BE')}`}>
                              par {invoice.validator_first_name || ''} {invoice.validator_last_name || ''}
                            </span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="fact-col-actions">
                          {invoice.pdf_path && (
                            <button
                              className="fact-action-btn download"
                              title="Telecharger PDF"
                              onClick={(e) => handleDownloadPdf(invoice, e)}
                            >
                              <ArrowDownTrayIcon />
                            </button>
                          )}
                          {/* Mark as paid - only for SENT (validated) invoices */}
                          {invoice.status === 'SENT' && (
                            <button
                              className="fact-action-btn paid"
                              title="Marquer paye"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <CheckCircleIcon />
                            </button>
                          )}
                          {/* Cancel - only for DRAFT/GENERATED (not SENT - already in accounting system) */}
                          {invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && invoice.status !== 'SENT' && (
                            <button
                              className="fact-action-btn cancel"
                              title="Annuler"
                              onClick={(e) => handleCancelInvoice(invoice, e)}
                            >
                              <XCircleIcon />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="fact-group-empty-affaires">
                    <p>Aucune facture generee pour ce contact.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Monthly Invoice Modal */}
        <GenerateMonthlyInvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          billingContact={getBillingContactForModal()}
          onSuccess={handleInvoiceSuccess}
          preselectedAffaireIds={Array.from(selectedAffaireIds)}
        />

        {/* PDF Preview Modal */}
        {showPdfPreview && previewInvoice && (
          <PdfPreviewModal
            document={{
              type: 'invoice',
              id: previewInvoice.id,
              number: previewInvoice.invoice_number || 'Brouillon',
              date: previewInvoice.invoice_date,
              clientName: previewInvoice.billing_name || '-',
              amount: previewInvoice.total_tvac,
              status: previewInvoice.status,
              hasPdf: !!previewInvoice.pdf_path,
              billingVatNumber: previewInvoice.billing_vat_number,
              structuredCommunication: previewInvoice.structured_communication,
              falcoStatus: previewInvoice.falco_status,
              falcoDocumentId: previewInvoice.falco_document_id,
              falcoSyncedAt: previewInvoice.falco_synced_at,
              falcoError: previewInvoice.falco_error,
              peppolStatus: previewInvoice.peppol_status,
              peppolDeliveredAt: previewInvoice.peppol_delivered_at,
              peppolError: previewInvoice.peppol_error,
              dueDate: previewInvoice.due_date,
            }}
            onClose={() => {
              setShowPdfPreview(false);
              setPreviewInvoice(null);
            }}
            onDownload={async () => {
              if (!previewInvoice) return;
              try {
                const blob = await downloadInvoicePdf(previewInvoice.id);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = previewInvoice.invoice_number
                  ? `facture_${previewInvoice.invoice_number.replace(/\//g, '-')}.pdf`
                  : `facture_${previewInvoice.id}.pdf`;
                a.click();
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
              } catch (error) {
                console.error('Download failed:', error);
              }
            }}
          />
        )}

        {/* Invoice Detail Modal */}
        {selectedInvoiceForDetail && (
          <InvoiceDetailModal
            invoice={selectedInvoiceForDetail}
            isOpen={true}
            onClose={() => setSelectedInvoiceForDetail(null)}
            onSuccess={() => {
              if (selectedContact) {
                queryClient.invalidateQueries({
                  queryKey: invoiceKeys.contactInvoices(selectedContact.contact_id)
                });
              }
            }}
          />
        )}
      </div>
    );
  }

  // Render contact list view
  return (
    <div className="fact-group-container">
      <div className="fact-group-intro">
        <h2>Facturation groupee</h2>
        <p>
          Selectionnez un client mensuel pour generer une facture groupee
          regroupant plusieurs dossiers.
        </p>
      </div>

      <div className="fact-group-contact-grid">
        {contacts.map((contact, index) => (
          <ContactCard
            key={`${contact.contact_id}-${index}`}
            contact={contact}
            onClick={() => handleContactSelect(contact)}
          />
        ))}
      </div>
    </div>
  );
};

export default GroupFacturationView;
